import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import * as R from 'ramda';
import {Provider} from 'mobx-react';
import CampaignGuidelinesListEmbed from 'js/components/campaign_guidelines_embed';
import {FeatureFlag} from 'react-launch-darkly';

import 'scss/approval_queues/post_review_grid.scss';

import {updateHash, getHashObject} from 'js/common/url_helper';
import ContentApprovalStore from 'js/approval_queues/state/content_approval_store';
import {CustomSQLGriddle, SORT_ASCENDING, SORT_DESCENDING} from 'js/components/griddle_datatable';
import {LeadRelationsByPlatformType} from 'js/common/lead_metadata_helper.js';
import Filters from './filters';
import ContentListRow from './content_row/list_row';
import {getRowDataForRowComponent} from 'js/common/griddle_column_helper';
import OptionsDropdown from 'js/components/options_dropdown';

export const FilterViews = {
    PENDING_POSTS: 'pending_posts',
    BRAND_REJECTED_POSTS: 'brand_rejected_posts',
    BRAND_APPROVED_POSTS: 'brand_approved_posts',
    VIEW_GUIDELINES: 'view_guidelines'
};

const sorts = {
    DESC: SORT_ASCENDING,
    ASC: SORT_DESCENDING
}

const contentSorts = [
    {id: sorts.DESC, label: 'Newest To Oldest'},
    {id: sorts.ASC, label: 'Oldest To Newest'}
]

const ContentReviewGuideline = ({guideline}) => (
    <div className="guidelines-body">
        <div className="guidelines-icon">{guideline.should === 0 ? 'x' : '✓'}</div>
        <div className="guidelines-text">{guideline.guideline}</div>
    </div>
);

const ContentReviewGuidelines = ({guidelines}) => {
    const doGuidelines = R.filter((guideline) => guideline.should === 1, guidelines);
    const dontGuidelines = R.filter((guideline) => guideline.should === 0, guidelines);

    return (
        <div className="content-review-list-guidelines">
            <div className="guidelines-type-container do-guidelines">
                <div className="guidelines-type-header">
                    <div className="guideline-type-icon do" />
                    Do&apos;s
                </div>
                <div className="guidelines-type-lister">
                    {doGuidelines.map((guideline, i) => (
                        <ContentReviewGuideline key={i} guideline={guideline} />
                    ))}
                </div>
            </div>
            <div className="guidelines-type-container dont-guidelines">
                <div className="guidelines-type-header">
                    <div className="guideline-type-icon dont" />
                    Dont&apos;s
                </div>
                <div className="guidelines-type-lister">
                    {dontGuidelines.map((guideline, i) => (
                        <ContentReviewGuideline key={i} guideline={guideline} />
                    ))}
                </div>
            </div>
        </div>
    );
};
ContentReviewGuidelines.propTypes = {
    guidelines: PropTypes.arrayOf(
        PropTypes.shape({
            should: PropTypes.oneOf([0, 1]).isRequired,
            guideline: PropTypes.number.isRequired
        })
    )
};

class ContentReviewListView extends Component {
    static propTypes = {
        campaign: PropTypes.shape({
            type: PropTypes.string.isRequired,
            id: PropTypes.string.isRequired
        }),
        handleReviewContentButtonClick: PropTypes.func.isRequired,
        guidelines: PropTypes.arrayOf(PropTypes.shape({})),
        url: PropTypes.string.isRequired
    };

    constructor(props) {
        super(props);

        const activeView = R.propOr(FilterViews.PENDING_POSTS, 'filter_view', getHashObject());
        this.state = {active_view: activeView, sort: sorts.DESC};

        const {campaign} = props;
        const leadRelations = LeadRelationsByPlatformType[campaign.type];
        this.renderLegacyGuidelines = this.renderLegacyGuidelines.bind(this);

        this.griddleColumns = [
            {
                field_alias: 'media_id',
                field_name: 'content_metadata.media_id',
                meta: {
                    displayName: 'Post',
                    searchable: false,
                    orderable: false
                }
            },
            {
                field_alias: 'caption',
                field_name: 'content_metadata.caption',
                meta: {
                    displayName: 'Caption',
                    searchable: false,
                    orderable: false
                }
            },
            {
                field_alias: 'admin_distribution_approval_status',
                field_name: 'content_metadata.post_status.admin_distribution_approval_status',
                meta: {
                    displayName: 'Admin Status',
                    searchable: false,
                    orderable: false
                }
            },
            {
                field_alias: 'brand_distribution_approval_status',
                field_name: 'content_metadata.post_status.brand_distribution_approval_status',
                meta: {
                    displayName: 'Brand Status',
                    searchable: false,
                    orderable: false
                }
            },
            {
                field_alias: 'username',
                // eslint-disable-next-line max-len
                field_name: `${leadRelations.METADATA_TABLE}.${leadRelations.METADATA_SOCIAL_UN_FIELD}`,
                meta: {
                    displayName: 'Username',
                    searchable: true,
                    orderable: false
                }
            },
            {
                field_alias: 'lead_id',
                field_name: 'brands_campaign_invite.lead_id',
                meta: {
                    displayName: 'Details',
                    searchable: false,
                    orderable: false
                }
            },
            {
                field_alias: 'created_at',
                field_name: 'content_metadata.created_at',
                meta: {
                    displayName: 'Created At',
                    searchable: false,
                    orderable: true
                }
            }
        ];
    }

    UNSAFE_componentWillUpdate(nextProps, nextState) {
        if (nextState.active_view !== this.state.active_view) {
            window.location.hash = updateHash({filter_view: nextState.active_view});
        }
    }

    getUrlModifier() {
        const {active_view} = this.state;

        switch (active_view) {
            case FilterViews.PENDING_POSTS: {
                return '';
            }
            case FilterViews.BRAND_APPROVED_POSTS: {
                const approvedStatus = this.state.approvedPostStatusIncludes;
                if (R.equals(approvedStatus, 'all') || R.isNil(approvedStatus)) {
                    return '?filter=brand_approved';
                }
                return `?filter=brand_approved&approvalStatus=${approvedStatus}`;
            }

            case FilterViews.BRAND_REJECTED_POSTS: {
                return '?filter=brand_rejected';
            }
            default: {
                return '';
            }
        }
    }

    updateCustomFilters = (nextState) => {
        this.setState(nextState);
    };

    onSort = (sort) => {
        this.setState({sort});
    };

    renderLegacyGuidelines = () => {
        return <ContentReviewGuidelines guidelines={this.props.guidelines} />;
    };

    render() {
        const {url, campaign, handleReviewContentButtonClick} = this.props;
        const {
            active_view, postStatusIncludes, approvedPostStatusIncludes, sort
        } = this.state;
        const initialState = {
            currentPageSize: 100,
            currentOrderDir: sort || sorts.DESC
        };

        const customGriddleConfiguration =
            active_view === FilterViews.BRAND_APPROVED_POSTS
                ? {approvedPostStatusIncludes}
                : {postStatusIncludes};

        return (
            <div id="distribution-review-grid">
                <div className="filter-buttons">
                    <button
                        className={classNames({
                            active: active_view === FilterViews.PENDING_POSTS
                        })}
                        onClick={() => this.setState({active_view: FilterViews.PENDING_POSTS})}
                    >
                        All
                    </button>
                    <button
                        className={classNames({
                            active: active_view === FilterViews.BRAND_REJECTED_POSTS
                        })}
                        onClick={() =>
                            this.setState({active_view: FilterViews.BRAND_REJECTED_POSTS})
                        }
                    >
                        Rejected
                    </button>
                    <button
                        className={classNames({
                            active: active_view === FilterViews.BRAND_APPROVED_POSTS
                        })}
                        onClick={() =>
                            this.setState({active_view: FilterViews.BRAND_APPROVED_POSTS})
                        }
                    >
                        Approved
                    </button>
                    <button
                        className={classNames({
                            active: active_view === FilterViews.VIEW_GUIDELINES
                        })}
                        onClick={() => this.setState({active_view: FilterViews.VIEW_GUIDELINES})}
                    >
                        View Guidelines
                    </button>
                </div>

                {active_view === FilterViews.VIEW_GUIDELINES ? (
                    <FeatureFlag
                        flagKey="guidelines-support-rich-text"
                        renderDefaultCallback={this.renderLegacyGuidelines}
                        renderFeatureCallback={(featureFlagValue) => {
                            if (featureFlagValue === 'on') {
                                return (
                                    <CampaignGuidelinesListEmbed
                                        application="admin_brand"
                                        isActionsMenuShown={false}
                                        isFilterable={false}
                                        campaignId={campaign.id}
                                    />
                                );
                            }

                            return this.renderLegacyGuidelines();
                        }}
                    />
                ) : (
                    <div className="post-review-list-ctn">
                        <div className="post-review-list-sort">
                            <OptionsDropdown
                                selected={sort}
                                options={contentSorts}
                                leftIcon={require('images/icons/svg/fa-sort.svg')}
                                onChange={this.onSort}
                            />
                        </div>
                        <div className="post-review-list-sort-ctn">
                            <Filters
                                active_view={this.state.active_view}
                                {...this.props}
                                postStatusIncludes={[]}
                                approvedPostStatusIncludes={[]}
                                updateFilterState={this.updateCustomFilters}
                            />
                        </div>
                        <CustomSQLGriddle
                            setting={false}
                            filter
                            loadOnMount
                            url={`${url}${this.getUrlModifier()}`}
                            columns={this.griddleColumns}
                            customConfiguration={customGriddleConfiguration}
                            initialState={initialState}
                            orderColumnName="created_at"
                            customRowComponent={(rowProps) => {
                                const rowData = getRowDataForRowComponent(rowProps);
                                const contentApprovalStore = new ContentApprovalStore(rowData);
                                return (
                                    <Provider contentApprovalStore={contentApprovalStore}>
                                        <ContentListRow
                                            {...rowProps}
                                            tableProps={{
                                                campaign,
                                                handleReviewContentButtonClick
                                            }}
                                            readOnly={
                                                this.state.active_view === FilterViews.PENDING_POSTS
                                            }
                                        />
                                    </Provider>
                                );
                            }}
                            classes={['content-review-list-griddle']}
                        />
                    </div>
                )}
            </div>
        );
    }
}

export default ContentReviewListView;



// WEBPACK FOOTER //
// ./approval_queues/components/content_review/list_view/index.js